import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
const KINDS = new Set(["internal", "customer", "service"]);
/**
 * Reads the claims of a token for DISPLAY and for hiding what the user cannot use. It does not verify the signature
 * and must never be trusted: the server decides every request. Returns null if the token is not shaped like ours.
 */
export function decodeClaims(token) {
    const parts = token.split(".");
    if (parts.length !== 3 || !parts[1])
        return null;
    try {
        const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const bin = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
        const c = JSON.parse(new TextDecoder().decode(Uint8Array.from(bin, (ch) => ch.charCodeAt(0))));
        if (typeof c.sub !== "string" || !KINDS.has(c.kind) || typeof c.exp !== "number")
            return null;
        if (!Array.isArray(c.roles) || !c.roles.every((r) => typeof r === "string"))
            return null;
        const tenant = c.tenant_id ?? null;
        if (tenant !== null && typeof tenant !== "string")
            return null;
        return { sub: c.sub, kind: c.kind, tenant_id: tenant, roles: c.roles, exp: c.exp };
    }
    catch {
        return null;
    }
}
export const hasAnyRole = (claims, roles) => !!claims && claims.roles.some((r) => roles.includes(r));
export const SessionContext = createContext(null);
const Ctx = SessionContext;
const KEY = "gweb.session.token";
const MAX_TIMER = 2 ** 31 - 1;
// Storage can be unavailable (private windows, blocked site data), so every access is guarded.
const read = () => { try {
    return sessionStorage.getItem(KEY);
}
catch {
    return null;
} };
const write = (v) => { try {
    if (v === null)
        sessionStorage.removeItem(KEY);
    else
        sessionStorage.setItem(KEY, v);
}
catch { /* ignore */ } };
/**
 * Holds the signed-in token for the current browser tab only (sessionStorage), and signs out when it expires.
 * This is the development sign-in. A real identity provider replaces it with a redirect flow and httpOnly cookies,
 * which is better than a token in script-readable storage; the rest of the app does not need to change.
 */
export function SessionProvider({ children, now = Date.now }) {
    const [token, setToken] = useState(() => {
        const t = read();
        const c = t ? decodeClaims(t) : null;
        if (t && (!c || c.exp * 1000 <= now())) {
            write(null);
            return null;
        }
        return t;
    });
    const claims = useMemo(() => (token ? decodeClaims(token) : null), [token]);
    const signOut = useCallback(() => { write(null); setToken(null); }, []);
    const signIn = useCallback((raw) => {
        const t = raw.trim().replace(/^Bearer\s+/i, "").replace(/\s+/g, "");
        const c = decodeClaims(t);
        if (!c)
            throw new Error("That does not look like a GWEB token.");
        if (c.exp * 1000 <= now())
            throw new Error("That token has expired. Mint a new one.");
        write(t);
        setToken(t);
    }, [now]);
    useEffect(() => {
        if (!claims)
            return;
        const timer = setTimeout(signOut, Math.min(Math.max(claims.exp * 1000 - now(), 0), MAX_TIMER));
        return () => clearTimeout(timer);
    }, [claims, signOut, now]);
    const value = useMemo(() => ({ token, claims, signIn, signOut }), [token, claims, signIn, signOut]);
    return _jsx(Ctx.Provider, { value: value, children: children });
}
export function useSession() {
    const v = useContext(Ctx);
    if (!v)
        throw new Error("useSession must be used inside SessionProvider");
    return v;
}
